import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, query, onSnapshot, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { UserCog, X, Trash2 } from 'lucide-react';
import { deleteToRecycleBin } from './recycleBinUtil';

const UserRoles = () => {
  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: '', role: 'employee', employeeId: '' });

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'userRoles')), (snap) => {
      setRoles(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'employees')), (snap) => {
      setEmployees(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  const employeeName = (id) => employees.find((e) => e.id === id)?.name || '-';

  const openAdd = () => {
    setForm({ email: '', role: 'employee', employeeId: '' });
    setShowForm(true);
  };

  const openEdit = (r) => {
    const role = r.role === 'admin' ? 'superadmin' : r.role; // migrate the old role value on next save
    setForm({ email: r.id, role, employeeId: r.employeeId || '' });
    setShowForm(true);
  };

  const saveRole = async () => {
    const email = form.email.trim().toLowerCase();
    if (!email || !email.includes('@')) return alert('Please enter a valid email -- this must be an email that can already log in to the app.');
    try {
      await setDoc(doc(db, 'userRoles', email), {
        role: form.role,
        employeeId: form.employeeId || null,
        updatedAt: serverTimestamp()
      }, { merge: true });
      setShowForm(false);
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const removeRole = async (r) => {
    if (!window.confirm(`Remove the assigned role for ${r.id}? They'll go back to the default (Employee, no HR/Payroll access).`)) return;
    await deleteToRecycleBin({ collectionName: 'userRoles', id: r.id, data: r, label: 'User Role' });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <UserCog size={18} color="#4a00e0" />
          <h3 style={{ margin: 0 }}>User Roles</h3>
        </div>
        <button onClick={openAdd} style={{ padding: '8px 16px', fontSize: '12px', borderRadius: '10px', border: 'none', background: 'linear-gradient(45deg, #4a00e0, #8e2de2)', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>+ Assign Role</button>
      </div>

      <p style={{ fontSize: '12px', color: '#888', marginBottom: '16px' }}>
        This only assigns what a login can see inside the app -- it does not create a login. The staff member's email/password must already exist (created by you, e.g. in the Firebase Console). Anyone not listed here defaults to "Employee" (no HR access). <b>Super Administrator</b> sees and can do everything, including HR. <b>Administrators</b> get everything an Employee already has, but cannot see the HR option.
      </p>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #f4f7fe', color: '#888', textAlign: 'left' }}>
            <th style={{ padding: '12px' }}>Email</th>
            <th>Role</th>
            <th>Linked Employee</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {roles.map((r) => {
            const roleLabel = (r.role === 'superadmin' || r.role === 'admin') ? 'Super Administrator' : r.role === 'administrator' ? 'Administrators' : 'Employee';
            const isElevated = roleLabel === 'Super Administrator';
            return (
            <tr key={r.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
              <td style={{ padding: '12px' }}>{r.id}</td>
              <td>
                <span style={{ padding: '3px 9px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold', background: isElevated ? '#f4f7fe' : roleLabel === 'Administrators' ? '#fff3cd' : '#f4f4f4', color: isElevated ? '#4a00e0' : roleLabel === 'Administrators' ? '#8a6d3b' : '#999' }}>
                  {roleLabel}
                </span>
              </td>
              <td>{r.employeeId ? employeeName(r.employeeId) : '-'}</td>
              <td>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={() => openEdit(r)} style={{ padding: '4px 10px', fontSize: '11px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Edit</button>
                  <button onClick={() => removeRole(r)} style={{ padding: '4px 8px', borderRadius: '6px', border: 'none', background: '#ffeded', color: 'red', cursor: 'pointer' }}><Trash2 size={13} /></button>
                </div>
              </td>
            </tr>
            );
          })}
          {roles.length === 0 && (
            <tr><td colSpan={4} style={{ textAlign: 'center', padding: '30px', color: '#999' }}>No roles assigned yet -- everyone defaults to Employee.</td></tr>
          )}
        </tbody>
      </table>

      {showForm && (
        <div onClick={() => setShowForm(false)} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', padding: '30px', borderRadius: '20px', width: '380px', maxWidth: '95%', position: 'relative' }}>
            <button onClick={() => setShowForm(false)} style={{ position: 'absolute', top: '15px', right: '15px', border: 'none', background: '#ffeded', color: 'red', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer' }}><X size={16} /></button>
            <h3 style={{ marginTop: 0 }}>Assign Role</h3>
            <input className="input-field" placeholder="Staff login email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <select className="input-field" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="employee">Employee</option>
              <option value="administrator">Administrators (everything except HR)</option>
              <option value="superadmin">Super Administrator (everything, including HR)</option>
            </select>
            <select className="input-field" value={form.employeeId} onChange={(e) => setForm({ ...form, employeeId: e.target.value })}>
              <option value="">-- Link to Employee record (optional) --</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
            <p style={{ fontSize: '11px', color: '#999', margin: '-8px 0 12px' }}>
              Linking connects this login to a Payroll employee, so their "My Profile" page shows that person's own payslip and leave balance. Leave this blank for a login that isn't a salaried employee (e.g. yourself as the owner).
            </p>
            <button onClick={saveRole} className="btn-primary" style={{ width: '100%', padding: '12px', marginTop: '5px' }}>Save</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRoles;
